/**
 * Membership & Feature Gating Constants
 * Notes+ - Bosph Technologies
 *
 * Bu dosya Free ve Plus üyelik planlarının limitlerini ve özellik erişimlerini tanımlar.
 * Offline-first mimarimize uygun olarak, bu değerler client-side kontrollerinde kullanılır.
 */

import i18n from '../i18n';

// Üyelik tipleri
export const MEMBERSHIP_TYPES = {
  FREE: 'free',
  PLUS: 'plus',
  ADMIN: 'admin',
};

// Dosya yükleme limitleri (byte) – Nginx global 50MB ile uyumlu
export const UPLOAD_LIMIT_FREE_BYTES = 15 * 1024 * 1024;  // 15MB
export const UPLOAD_LIMIT_PLUS_BYTES = 50 * 1024 * 1024;  // 50MB

// Free plan limitleri
export const FREE_LIMITS = {
  // Not limitleri — single source of truth for Free max notes (UI + client gates).
  // Server mirror: server/src/config/membershipLimits.js → FREE_MAX_NOTE_COUNT
  maxNoteCount: 20,
  mediaInNotes: false,
  
  // Çöp kutusu
  trashRetentionDays: 7,
  
  // Tema erişimi (tek tema: midnight)
  allowedThemes: ['midnight'],
  
  // Takvim limitleri
  maxCalendars: 1,
  recurringEvents: false,
  
  // Mola hatırlatıcı
  customBreakSounds: false,
  
  // Senkronizasyon
  autoSync: false, // Manuel sync (şimdilik sadece flag)
  
  // Dosya yükleme (chat, profil vb.)
  maxUploadBytes: UPLOAD_LIMIT_FREE_BYTES,
};

// Plus plan limitleri (sınırsız veya aktif)
export const PLUS_LIMITS = {
  // Not limitleri
  maxNoteCount: Infinity,
  mediaInNotes: true,
  
  // Çöp kutusu
  trashRetentionDays: 30,
  
  // Tema erişimi (tek tema: midnight)
  allowedThemes: ['midnight'],
  
  // Takvim limitleri
  maxCalendars: Infinity,
  recurringEvents: true,
  
  // Mola hatırlatıcı
  customBreakSounds: true,
  
  // Senkronizasyon
  autoSync: true,
  
  // Dosya yükleme
  maxUploadBytes: UPLOAD_LIMIT_PLUS_BYTES,
};

// Admin her zaman Plus gibi davranır
export const ADMIN_LIMITS = { ...PLUS_LIMITS };

/**
 * Kullanıcının üyelik tipine göre limitleri döndürür
 * @param {string} membershipType - 'free', 'plus', veya 'admin'
 * @returns {object} Limit objesi
 */
export const getLimitsForMembership = (membershipType) => {
  switch (membershipType) {
    case MEMBERSHIP_TYPES.ADMIN:
      return ADMIN_LIMITS;
    case MEMBERSHIP_TYPES.PLUS:
      return PLUS_LIMITS;
    case MEMBERSHIP_TYPES.FREE:
    default:
      return FREE_LIMITS;
  }
};

/**
 * Belirli bir özellik için limit değerini döndürür
 * @param {string} membershipType - Üyelik tipi
 * @param {string} featureName - Özellik adı (örn: 'maxNoteCount', 'allowedThemes')
 * @returns {any} Limit değeri
 */
export const getFeatureLimit = (membershipType, featureName) => {
  const limits = getLimitsForMembership(membershipType);
  return limits[featureName];
};

/**
 * Kullanıcının Plus özelliklerine erişimi olup olmadığını kontrol eder
 * @param {string} membershipType - Üyelik tipi
 * @returns {boolean}
 */
export const hasPlusAccess = (membershipType) => {
  return membershipType === MEMBERSHIP_TYPES.PLUS || membershipType === MEMBERSHIP_TYPES.ADMIN;
};

/**
 * Kullanıcının belirli bir temayı kullanıp kullanamayacağını kontrol eder
 * @param {string} membershipType - Üyelik tipi
 * @param {string} themeName - Tema adı ('midnight')
 * @returns {boolean}
 */
export const canUseTheme = (membershipType, themeName) => {
  const limits = getLimitsForMembership(membershipType);
  return limits.allowedThemes.includes(themeName);
};

// Feature adları için sabitler (typo önleme)
export const FEATURE_NAMES = {
  MAX_NOTE_COUNT: 'maxNoteCount',
  MEDIA_IN_NOTES: 'mediaInNotes',
  TRASH_RETENTION_DAYS: 'trashRetentionDays',
  ALLOWED_THEMES: 'allowedThemes',
  MAX_CALENDARS: 'maxCalendars',
  RECURRING_EVENTS: 'recurringEvents',
  CUSTOM_BREAK_SOUNDS: 'customBreakSounds',
  AUTO_SYNC: 'autoSync',
};

// Plus'a yükseltme mesajları — i18n.t() her erişimde çağrılır ki dil değişince güncel kalsın
const UPGRADE_MESSAGE_KEYS = [
  'noteLimit',
  'calendarLimit',
  'recurringEvents',
  'mediaInNotes',
  'customSounds',
  'uploadLimit',
  'themeLimit',
];

export const UPGRADE_MESSAGES = UPGRADE_MESSAGE_KEYS.reduce((acc, key) => {
  Object.defineProperty(acc, key, {
    enumerable: true,
    get: () => i18n.t(`shared:upgradeMessages.${key}`),
  });
  return acc;
}, {});

export default {
  MEMBERSHIP_TYPES,
  FREE_LIMITS,
  PLUS_LIMITS,
  ADMIN_LIMITS,
  getLimitsForMembership,
  getFeatureLimit,
  hasPlusAccess,
  canUseTheme,
  FEATURE_NAMES,
  UPGRADE_MESSAGES,
};














